import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { StorageCenter } from '../../models/storage.center.model';
import { Variant } from '../../models/variant.model';
import { GlobalSwal } from '../../styles/swal.style';
import { StorageOperationDA } from '../../services/admin/storage.operation.service';
import { ProductDA } from '../../services/admin/product.service';
import { Error } from '../../models/error.model';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendValid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { storageCenterSelected } from '../../scripts/list.selected.script';

import AppLoading from '../Loading';
import AppLegend from '../element/Legend';

export interface AppModalStorageTransferProps {
  companyForUser: Company | null,
  storageCentersForCompany: StorageCenter[] | null,
  onSuccess: () => void
};

interface TransferLine {
  id_variant: string;
  display_name: string;
  stock: number;
  quantity: number;
}

let errorResponse: Error;

const AppModalStorageTransfer: React.FunctionComponent<AppModalStorageTransferProps> = ({companyForUser, storageCentersForCompany, onSuccess}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [loadingVariants, setLoadingVariants] = useState(false)
  const [sourceCenter, setSourceCenter] = useState({value: '', valid: false})
  const [destCenter, setDestCenter] = useState({value: '', valid: false})
  const [variants, setVariants] = useState<Variant[]>([])
  const [lines, setLines] = useState<TransferLine[]>([])
  const [searchProduct, setSearchProduct] = useState('')

  const modalId = 'modal-storage-transfer'
  const hasLines = lines.some((line) => line.quantity > 0)

  const availableVariants = variants.filter((v) => {
    if (v.stock <= 0) return false
    return !lines.some((l) => l.id_variant === v.id)
  })

  const filteredVariants = availableVariants.filter((v) => {
    if (!searchProduct) return true
    const search = searchProduct.toLowerCase()
    return (v.display_name ?? '').toLowerCase().includes(search) ||
           (v.description_short ?? '').toLowerCase().includes(search) ||
           (v.code ?? '').toLowerCase().includes(search)
  })

  const destCenters = (storageCentersForCompany ?? []).filter((c) => c.id !== sourceCenter.value)

  const handleChangeSourceCenter = (item: string) => {
    setSourceCenter({value: item, valid: true})
    setLines([])
    if (destCenter.value === item) {
      setDestCenter({value: '', valid: false})
    }
    legendValid(`${modalId}-legend-source-center-required`)
    loadVariantsForCenter(item)
  }

  const handleChangeDestCenter = (item: string) => {
    setDestCenter({value: item, valid: true})
    legendValid(`${modalId}-legend-dest-center-required`)
  }

  const loadVariantsForCenter = async (id_storage_center: string) => {
    if (!companyForUser) return
    setLoadingVariants(true)

    try {
      const response = await ProductDA.getProductVariants(companyForUser.id, id_storage_center, '', '', '')
      if (response.status === 200) {
        const data = response.data?.data ?? response.data
        let items = data.items ?? data.product_variants ?? []

        if (items.length > 0) {
          const variantIds = items.map((v: any) => v.id)
          const stockResponse = await StorageOperationDA.getStockAvailability(companyForUser.id, variantIds, id_storage_center)
          if (stockResponse.status === 200) {
            const stockData = stockResponse.data
            items = items.map((v: any) => {
              const variantStock = stockData[v.id]
              if (variantStock && Array.isArray(variantStock)) {
                const centerStock = variantStock.find((s: any) => s.id_storage_center === id_storage_center)
                return { ...v, stock: centerStock?.quantity ?? 0 }
              }
              return { ...v, stock: 0 }
            })
          }
        }

        setVariants(items)
      }
    } catch {
      setVariants([])
    } finally {
      setLoadingVariants(false)
    }
  }

  const handleAddLine = (variant: Variant) => {
    setLines([...lines, {
      id_variant: variant.id,
      display_name: `${variant.display_name ?? ''}: ${variant.description_short ?? ''}`,
      stock: variant.stock ?? 0,
      quantity: 1
    }])
    setSearchProduct('')
  }

  const handleChangeQuantity = (index: number, value: string) => {
    const numValue = Math.max(0, Math.min(Number(value) || 0, lines[index].stock))
    setLines(lines.map((line, i) => i === index ? {...line, quantity: numValue} : line))
  }

  const handleRemoveLine = (index: number) => {
    setLines(lines.filter((_, i) => i !== index))
  }

  const submitTransfer = async () => {
    setLoadIndicator('on')

    if (!sourceCenter.valid || !destCenter.valid) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!sourceCenter.value.length) legendInvalid(`${modalId}-legend-source-center-required`)
        if (!destCenter.value.length) legendInvalid(`${modalId}-legend-dest-center-required`)
        setLoadIndicator('off')
      })
      return
    }

    if (!hasLines) {
      GlobalSwal.fire({
        text: lang.labels.selectAtLeastOneQuantity,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        setLoadIndicator('off')
      })
      return
    }

    if (companyForUser) {
      const lineItems = lines
        .filter((line) => line.quantity > 0)
        .map((line) => ({id_variant: line.id_variant, quantity: line.quantity}))

      await StorageOperationDA.postStorageOperations(companyForUser.id, 'transfer', null, sourceCenter.value, destCenter.value, lineItems).then((response) => {
        if (response.status === 201) {
          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedStorageOperations,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then(() => {
            executeHideModal()
            onSuccess()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch((error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModal = () => {
    modalHide(modalId)
    setTimeout(() => { restartModal() }, 200)
  }

  function restartModal() {
    legendValidInvalidRestart(`${modalId}-legend-source-center-required`)
    legendValidInvalidRestart(`${modalId}-legend-dest-center-required`)
    setSourceCenter({value: '', valid: false})
    setDestCenter({value: '', valid: false})
    setVariants([])
    setLines([])
    setSearchProduct('')
  }

  return (
    <div id={modalId} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.stockTransfer}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && storageCentersForCompany
              ?
              <div className="form">
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.sourceStorageCenter}</label>
                  <Dropdown>
                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                      <span className="selection">
                        <span className="select2-selection select2-selection--single form-select">
                          <span className="select2-selection__rendered" role="textbox">
                            {sourceCenter.valid ? storageCenterSelected(storageCentersForCompany, sourceCenter.value) : lang.labels.selectOption}
                          </span>
                        </span>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                      <span className="select2-dropdown select2-dropdown--below">
                        <span className="select2-results">
                          <ul className="select2-results__options" role="listbox">
                            { storageCentersForCompany.map (( (item, index) => { return (
                              <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === sourceCenter.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === sourceCenter.value}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeSourceCenter(item.id)}>{item.name}</Dropdown.Item>
                              </li>
                            )}))}
                          </ul>
                        </span>
                      </span>
                    </Dropdown.Menu>
                  </Dropdown>
                  <AppLegend component={modalId} attribute={{check: sourceCenter.valid, name: 'source-center', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.destinationStorageCenter}</label>
                  <Dropdown>
                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                      <span className="selection">
                        <span className="select2-selection select2-selection--single form-select">
                          <span className="select2-selection__rendered" role="textbox">
                            {destCenter.valid ? storageCenterSelected(storageCentersForCompany, destCenter.value) : lang.labels.selectOption}
                          </span>
                        </span>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                      <span className="select2-dropdown select2-dropdown--below">
                        <span className="select2-results">
                          <ul className="select2-results__options" role="listbox">
                            { destCenters.map (( (item, index) => { return (
                              <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === destCenter.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === destCenter.value}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeDestCenter(item.id)}>{item.name}</Dropdown.Item>
                              </li>
                            )}))}
                          </ul>
                        </span>
                      </span>
                    </Dropdown.Menu>
                  </Dropdown>
                  <AppLegend component={modalId} attribute={{check: destCenter.valid, name: 'dest-center', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                { sourceCenter.valid &&
                  <>
                    { loadingVariants
                      ?
                      <AppLoading></AppLoading>
                      :
                      <>
                        <div className="dataTables_wrapper mb-5">
                          <table className="table align-middle table-row-dashed gy-1 mb-2">
                            <thead className="border-bottom">
                              <tr className="text-muted text-uppercase fw-bolder fs-7">
                                <th className="min-w-150px text-nowrap text-start">{lang.labels.product}</th>
                                <th className="w-80px text-nowrap text-center">{lang.labels.availableStock}</th>
                                <th className="w-100px text-nowrap text-center">{lang.labels.quantityToTransfer}</th>
                                <th className="w-50px text-nowrap text-center">{lang.labels.actions}</th>
                              </tr>
                            </thead>
                            <tbody className="text-muted fw-bold">
                              { lines.map (( (item, index) => { return (
                                <tr key={index}>
                                  <td className="text-secondary text-start">{item.display_name}</td>
                                  <td className="text-center">{item.stock}</td>
                                  <td className="text-center">
                                    <input
                                      className="form-control text-center w-80px mx-auto"
                                      type="number"
                                      min={0}
                                      max={item.stock}
                                      value={item.quantity}
                                      onChange={(event) => handleChangeQuantity(index, event.target.value)}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <button className="btn btn-sm btn-icon btn-light-danger" type="button" onClick={() => handleRemoveLine(index)}>
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              )}))}
                              { lines.length === 0 &&
                                <tr>
                                  <td colSpan={4} className="text-center text-muted">{lang.labels.noRecordsFound}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                        <div className="fv-row">
                          <Dropdown>
                            <Dropdown.Toggle variant="btn btn-sm btn-light-primary w-100">
                              <i className="bi bi-plus-lg me-1"></i>
                              {lang.labels.addLine}
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-100 p-3 fs-7 mh-300px overflow-auto">
                              <div className="mb-2">
                                <input
                                  className="form-control form-control-sm"
                                  type="text"
                                  placeholder={lang.labels.searchProduct}
                                  value={searchProduct}
                                  onChange={(e) => setSearchProduct(e.target.value)}
                                  autoComplete="off"
                                />
                              </div>
                              { filteredVariants.length > 0
                                ?
                                filteredVariants.slice(0, 50).map((item, index) => (
                                  <Dropdown.Item key={index} bsPrefix="menu-item" onClick={() => handleAddLine(item)}>
                                    <p className="menu-link fw-bold px-3 py-1 m-0">
                                      {item.display_name}: {item.description_short}
                                      {` (${item.stock})`}
                                    </p>
                                  </Dropdown.Item>
                                ))
                                :
                                <div className="text-center text-muted py-3">{lang.labels.noRecordsFound}</div>
                              }
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </>
                    }
                  </>
                }
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.noCancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitTransfer} disabled={!hasLines}>
              <span className="indicator-label">{lang.labels.transfer}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalStorageTransfer;
